import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Check, ChevronRight } from 'lucide-react';
import { DateField, TextField } from '@/components/Field';
import { UnitField } from '@/components/UnitField';
import { Segmentado } from '@/components/Segmentado';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { leerPesajes } from '@/db/pesajes';
import {
  validarEstatura,
  validarPerfil,
  type CambiosPerfil,
  type CampoPerfil,
} from '@/domain/perfil';
import { formatearNumero } from '@/domain/numbers';
import { fechaCorta } from '@/domain/hoy';
import { resumenDePeso } from '@/domain/tendencia';
import {
  UNIDADES_LONGITUD,
  UNIDADES_PESO,
  desdeCm,
  esUnidadLongitud,
  formatearPeso,
  type Capturado,
  type UnidadLongitud,
  type UnidadPeso,
} from '@/domain/units';
import {
  DESCRIPCION_ACTIVIDAD,
  NIVELES_ACTIVIDAD,
  type NivelActividad,
  type Settings,
  type Sexo,
} from '@/db/types';
import type { FechaLocal } from '@/domain/dates';
import { Opciones } from '@/features/food/comunes';

/** Lo que el formulario escribe: el perfil y, si cambió lo escrito, la estatura. */
export type GuardadoDePerfil = CambiosPerfil & {
  estaturaCm?: number | null;
  estaturaCapturada?: Capturado | null;
};

interface Props {
  settings: Settings;
  textoGuardar: string;
  onGuardar: (cambios: GuardadoDePerfil) => Promise<void>;
  /** Acción secundaria, por ejemplo "Saltar por ahora" en Primeros pasos. */
  secundaria?: ReactNode;
  hoy: FechaLocal;
}

const SEXOS: readonly { valor: Sexo | ''; etiqueta: string }[] = [
  { valor: '', etiqueta: 'Sin dato' },
  { valor: 'mujer', etiqueta: 'Mujer' },
  { valor: 'hombre', etiqueta: 'Hombre' },
];

/** El número tal como se escribe en un campo: sin separador de miles. */
const paraCampo = (n: number) => formatearNumero(n, { decimales: 1, fijos: false }).replace(/,/g, '');

/**
 * Formulario del perfil, compartido por Primeros pasos y Más → Perfil
 * (Req. 4.1 a 4.6, 5.1).
 *
 * Formulario completo (0.11.0): pregunta solo lo que alguna pantalla lee o
 * algún cálculo usa hoy. El nombre saluda en Hoy y nombra los respaldos; las
 * unidades deciden cómo se ve todo; la estatura da el IMC; la fecha de
 * nacimiento, el sexo y el nivel de actividad son los tres datos que faltan
 * para el mantenimiento estimado. De cada uno se dice para qué se pide.
 *
 * Todo sigue siendo opcional (Req. 4.2) y todo llega precargado: guardar no
 * reinicia el perfil ni borra registros. El peso de referencia no se pregunta
 * aquí porque un peso lleva báscula, fecha y hora: se registra en Cuerpo y
 * este bloque solo dice cuál es el último.
 */
export function FormularioPerfil({ settings, textoGuardar, onGuardar, secundaria, hoy }: Props) {
  const [nombre, setNombre] = useState(settings.nombre ?? '');
  const [unidadPeso, setUnidadPeso] = useState<UnidadPeso>(settings.unidadPeso);
  const [unidadLongitud, setUnidadLongitud] = useState<UnidadLongitud>(settings.unidadLongitud);
  const [nacimiento, setNacimiento] = useState(settings.fechaNacimiento ?? '');
  const [sexo, setSexo] = useState<Sexo | ''>(settings.sexo ?? '');
  const [actividad, setActividad] = useState<NivelActividad | ''>(settings.nivelActividad ?? '');

  // La estatura, con la unidad en que se escribió (Req. 5.4)
  const escrita =
    settings.estaturaCapturada !== null &&
    esUnidadLongitud(settings.estaturaCapturada.unidad) &&
    Number.isFinite(settings.estaturaCapturada.valor)
      ? settings.estaturaCapturada
      : null;
  const unidadEstaturaInicial: UnidadLongitud =
    escrita !== null && esUnidadLongitud(escrita.unidad) ? escrita.unidad : settings.unidadLongitud;
  const estaturaInicial =
    escrita !== null
      ? paraCampo(escrita.valor)
      : settings.estaturaCm !== null
        ? paraCampo(desdeCm(settings.estaturaCm, settings.unidadLongitud))
        : '';
  const [estatura, setEstatura] = useState(estaturaInicial);
  const [unidadEstatura, setUnidadEstatura] = useState<UnidadLongitud>(unidadEstaturaInicial);

  const [errores, setErrores] = useState<Partial<Record<CampoPerfil | 'estatura', string>>>({});
  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState<string | null>(null);

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    if (guardando) return;
    setErrorGuardar(null);

    const resultado = validarPerfil({ nombre, unidadPeso, unidadLongitud, fechaNacimiento: nacimiento, sexo, nivelActividad: actividad }, hoy);
    // Sin cambios en lo escrito, la estatura queda exactamente como estaba:
    // abierta en otra unidad y guardada, 164 pasaba a 164.08 cm (0.4.0)
    const estaturaIntacta = estatura.trim() === estaturaInicial && unidadEstatura === unidadEstaturaInicial;
    const deEstatura = estaturaIntacta ? null : validarEstatura(estatura, unidadEstatura);

    if (!resultado.valido || (deEstatura !== null && !deEstatura.valido)) {
      setErrores({
        ...(resultado.valido ? {} : resultado.errores),
        ...(deEstatura !== null && !deEstatura.valido ? { estatura: deEstatura.error } : {}),
      });
      return;
    }

    setErrores({});
    setGuardando(true);
    try {
      await onGuardar({
        ...resultado.cambios,
        ...(deEstatura === null
          ? {}
          : (deEstatura.valor ?? { estaturaCm: null, estaturaCapturada: null })),
      });
    } catch (error) {
      setErrorGuardar(error instanceof Error ? error.message : 'No se pudo guardar el perfil.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={(e) => void guardar(e)} className="grid gap-6" noValidate>
      <Bloque titulo="Tu nombre">
        <TextField
          etiqueta="Nombre"
          valor={nombre}
          onCambio={setNombre}
          error={errores.nombre}
          maxLength={60}
          placeholder="Cómo quieres que te salude la app"
          ayuda="Te saluda en Hoy y nombra tus respaldos."
        />
      </Bloque>

      <Bloque
        titulo="Unidades para ver tus datos"
        ayuda="Puedes capturar en cualquier unidad; la app convierte y te muestra todo en estas. El peso también es la unidad inicial de las cargas en Gym, y después cada ejercicio recuerda la suya."
      >
        <div className="flex flex-wrap gap-4">
          <Segmentado
            etiqueta="Peso"
            etiquetaVisible
            valor={unidadPeso}
            opciones={UNIDADES_PESO.map((u) => ({ valor: u, etiqueta: u }))}
            onCambio={setUnidadPeso}
          />
          <Segmentado
            etiqueta="Medidas"
            etiquetaVisible
            valor={unidadLongitud}
            opciones={UNIDADES_LONGITUD.map((u) => ({ valor: u, etiqueta: u }))}
            onCambio={setUnidadLongitud}
          />
        </div>
      </Bloque>

      <Bloque titulo="Tu estatura" ayuda="Con ella se calcula tu IMC en Cuerpo y entra en el mantenimiento estimado. Sin estatura, el IMC no se muestra.">
        <UnitField
          etiqueta="Estatura"
          magnitud="longitud"
          valor={estatura}
          onCambio={(v) => {
            setEstatura(v);
            setErrores(({ estatura: _, ...resto }) => resto);
          }}
          unidad={unidadEstatura}
          onCambioUnidad={setUnidadEstatura}
          unidadPreferida={unidadLongitud}
          error={errores.estatura}
          placeholder={unidadEstatura === 'in' ? '63' : '160'}
          {...(settings.estaturaCm !== null ? { ayuda: 'Para quitarla, borra el número y guarda.' } : {})}
        />
      </Bloque>

      <Bloque
        titulo="Tu edad y tu sexo"
        ayuda="La fórmula del mantenimiento estimado usa los dos; no se usan para nada más. Si no los pones, el mantenimiento se captura a mano."
      >
        <DateField
          etiqueta="Fecha de nacimiento"
          valor={nacimiento}
          onCambio={setNacimiento}
          max={hoy}
          error={errores.fechaNacimiento}
        />
        <Opciones etiqueta="Sexo" valor={sexo} opciones={SEXOS} onCambio={setSexo} />
      </Bloque>

      <Bloque
        titulo="Tu nivel de actividad"
        ayuda="Es el factor por el que se multiplica tu metabolismo para estimar tu mantenimiento: de ahí salen las kcal que luego puedes aceptar o rechazar. Agrupa todo tu movimiento del día, incluido lo que entrenas; no lo mide. Si dudas entre dos, elige el de menos: así la propuesta no queda alta."
      >
        <Opciones
          etiqueta="Nivel de actividad"
          valor={actividad}
          opciones={[
            { valor: '' as const, etiqueta: 'Sin dato' },
            ...NIVELES_ACTIVIDAD.map((n) => ({
              valor: n,
              etiqueta: `${DESCRIPCION_ACTIVIDAD[n].nombre} · ${DESCRIPCION_ACTIVIDAD[n].descripcion.toLocaleLowerCase('es-MX')}`,
            })),
          ]}
          onCambio={setActividad}
        />
      </Bloque>

      <BloqueDePeso unidad={unidadPeso} basculaTendenciaId={settings.basculaTendenciaId} hoy={hoy} />

      {Object.keys(errores).length > 0 && (
        <Banner tono="alerta" titulo="Revisa los campos marcados">
          No se guardó nada todavía.
        </Banner>
      )}
      {errorGuardar !== null && (
        <Banner tono="alerta" titulo="No se pudo guardar">
          {errorGuardar}
        </Banner>
      )}

      <div className="space-y-2">
        <Button type="submit" disabled={guardando} icono={Check}>
          {guardando ? 'Guardando…' : textoGuardar}
        </Button>
        {secundaria}
      </div>
    </form>
  );
}

/** Un bloque del formulario: su título, para qué se pide y sus campos. */
function Bloque({ titulo, ayuda, children }: { titulo: string; ayuda?: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-[17px] tracking-[-0.25px] text-texto">{titulo}</h2>
        {ayuda !== undefined && <p className="mt-1 text-[12px] leading-relaxed text-texto-suave">{ayuda}</p>}
      </div>
      {children}
    </section>
  );
}

/**
 * El peso de referencia (Req. 14): no se escribe aquí porque un peso lleva su
 * báscula, su fecha y su hora, y así entra en la tendencia y en el IMC. El
 * bloque dice cuál es el último y lleva a registrar uno.
 */
function BloqueDePeso({
  unidad,
  basculaTendenciaId,
  hoy,
}: {
  unidad: UnidadPeso;
  basculaTendenciaId: string | null;
  hoy: FechaLocal;
}) {
  const pesajes = useLiveQuery(() => leerPesajes(), []);
  if (pesajes === undefined) return null;
  const ultimo = resumenDePeso(pesajes, basculaTendenciaId, hoy).ultimo;

  return (
    <Bloque
      titulo="Tu peso de referencia"
      ayuda="Tu peso sale de tus pesajes, con la báscula y la fecha de cada uno: así se calculan tu tendencia de 7 días y tu IMC. Se registra en Cuerpo, no aquí."
    >
      <Link
        to="/cuerpo/pesaje/nuevo"
        className="flex min-h-14 w-full items-center gap-3 rounded-panel bg-superficie px-4 py-3 text-left active:opacity-60"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-medium text-texto">
            {ultimo === null ? 'Registrar mi peso' : `Último: ${formatearPeso(ultimo.pesoKg, unidad)}`}
          </span>
          <span className="block text-[12px] text-texto-suave">
            {ultimo === null ? 'Todavía no tienes pesajes' : fechaCorta(ultimo.fecha)}
          </span>
        </span>
        <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-texto-suave" />
      </Link>
    </Bloque>
  );
}
